import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useFetch, Context } from 'store';

import { Button, Icon } from 'antd';
import moment from 'moment';

import { AsyncTable } from '_src/components';
import { PersistentRepo } from '_src/shared/repo';
import { Restricted } from '_src/shared/restrict';
import { debounce } from '_src/utils';
import services from '_src/services';
import { RangePicker, Select, Input } from '_src/shared/form-helpers';
import { PageWrapper, ActionRow } from '_src/shared/styles';
import { camelToSnakeCase } from '../../utils/helpers';

import { PageTitle, Label, Search } from './styles';

const baseStatusOptions = [
  { id: 0, label: 'All' },
  { id: 1, label: 'Success' },
  { id: 2, label: 'Declined' },
  { id: 3, label: 'Fail' }
];

const locationTypes = [
  { id: 0, label: 'All' },
  { id: 1, label: 'Yard' },
  { id: 2, label: 'Classroom' }
];

export const useSubmissionReportFilters = ({ includePendingStatus = false } = {}) => {
  const locationsConfig = services['studentRecords'];
  const statusOptions = includePendingStatus
    ? [...baseStatusOptions, { id: 4, label: 'Pending' }]
    : baseStatusOptions;

  const { getData } = useContext(Context);

  const getInitialLocation = () => {
    const allBranchesSelected = PersistentRepo.get('allBranches');

    const location = allBranchesSelected ? allBranchesSelected : PersistentRepo.get('location');
    const user = getData('user');
    const branchId = user.branchId || 1;

    return location ? location : `allxallx${branchId}`;
  };

  const getInitialId = type => {
    const [locationType, locationId, branchId] = getInitialLocation().split('x');
    return type === 'locationType' ? locationType : branchId;
  };

  const getInitialLocationTypeValue = () => {
    const initial = getInitialId('locationType');
    return initial === 'all' ? 0 : initial === 'yard' ? 1 : 2;
  };

  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(getInitialLocation());
  const [locType, setLocType] = useState(getInitialLocationTypeValue());
  const [submissioner, setSubmissioner] = useState('');
  const [student, setStudent] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState(0);

  const { get: getLocations } = useFetch({ ...locationsConfig.getLocations });

  const [calendarDates, setCalendarDates] = useState({
    startDate: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });

  const [filterParams, setFilterParams] = useState({
    branchId: getInitialId('branchId'),
    locationType: getInitialLocationTypeValue(),
    startDate: calendarDates.startDate,
    endDate: calendarDates.endDate
  });

  const debounceCallback = useCallback(
    debounce(
      params => {
        setFilterParams(params);
      },
      1000,
      false
    ),
    []
  );

  const disabledDate = current => current.isAfter(moment(), 'day');

  useEffect(() => {
    getLocations().then(data => {
      setLocations([{ id: `all`, label: `All Branches` }, ...data]);
    });
  }, []);

  const onChange = ([startDate, endDate]) => {
    const range = {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    };
    setCalendarDates({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    });
    setFilterParams({ ...filterParams, ...range });
  };

  const renderFilters = extra => (
    <Search>
      <div>
        <Label>Select date range to view submissions</Label>
        <RangePicker
          allowClear={false}
          ranges={{
            Today: [moment(), moment()],
            'Last Week': [moment().subtract(7, 'd'), moment()],
            'Last Month': [moment().subtract(1, 'months'), moment()]
          }}
          defaultValue={[moment().subtract(1, 'months'), moment()]}
          onChange={onChange}
          disabledDate={disabledDate}
        />
      </div>

      <Select
        label="Branches"
        showSearch
        onChange={locationSelected => {
          setLocation(locationSelected);
          if (locationSelected === 'all') {
            PersistentRepo.set('allBranches', locationSelected);
            setFilterParams(() => {
              return {
                startDate: calendarDates.startDate,
                endDate: calendarDates.endDate,
                locationType: locType
              };
            });
          } else {
            const [locationType, locationId, branchId] = locationSelected.split('x');
            const definedLocationType = locationSelected
              ? locationType === 'all'
                ? 0
                : locationType === 'yard'
                  ? 1
                  : 2
              : getInitialLocationTypeValue();
            setLocType(definedLocationType ? definedLocationType : locType);

            setFilterParams(() => {
              return {
                ...filterParams,
                branchId: locationSelected ? branchId : getInitialId('branchId'),
                locationType: definedLocationType ? definedLocationType : locType
              };
            });
            PersistentRepo.set('location', locationSelected);
            PersistentRepo.delete('allBranches');
          }
        }}
        options={locations}
        value={location}
      />

      <Select
        label="Branch Type"
        onChange={e => {
          setLocType(e);
          setFilterParams({
            ...filterParams,
            locationType: e
          });
        }}
        disabled={getInitialId('locationType') !== 'all'}
        options={locationTypes}
        value={locType}
      />

      <Input
        label="Submitted By"
        onChange={e => {
          setSubmissioner(e);
          debounceCallback({ ...filterParams, submittedBy: e });
        }}
        value={submissioner}
      />

      <Input
        label="Student"
        onChange={e => {
          setStudent(e);
          debounceCallback({ ...filterParams, student: e });
        }}
        value={student}
      />

      <Select
        label="Submission Status"
        onChange={val => {
          setSubmissionStatus(val);

          if (val === 0) {
            delete filterParams.status;

            debounceCallback(filterParams);
            return;
          }

          debounceCallback({
            ...filterParams,
            status: statusOptions.find(({ id }) => id === val).label.toLowerCase()
          });
        }}
        options={statusOptions}
        value={submissionStatus}
      />
      {extra}
    </Search>
  );

  return { renderFilters, filterParams, setFilterParams, calendarDates, locations };
};

const StandaloneSubmissionReport = ({ isReusable }) => {
  const baseApi = GLOBALS.BASE_API;
  const resource = 'submissionReport';
  const config = services[resource];
  const token = PersistentRepo.get('token');

  const { renderFilters, filterParams, setFilterParams, calendarDates, locations } =
    useSubmissionReportFilters();

  const downloadSubmissionReport = () => {
    const { branchId, submittedBy, student, status } = filterParams;
    const currentBranchId = branchId ? parseInt(branchId, 10) : null;

    const params = {
      start_date: calendarDates.startDate,
      end_date: calendarDates.endDate,
      ...(branchId ? { branch_id: currentBranchId } : {}),

      ...(submittedBy?.length ? { submittedBy: submittedBy } : {}),
      ...(student?.length ? { student: student } : {}),
      ...(status ? { status: status } : {})
    };

    fetch(`${baseApi}/${config.downloadSubmissionReport.path}`, {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })
      .then(function (resp) {
        return resp.blob();
      })
      .then(function (blob) {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `Submission_Report_${calendarDates.startDate}_${calendarDates.endDate}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        return;
      });
  };
  return (
    <PageWrapper style={{ padding: isReusable ? 0 : '25px' }}>
      {!isReusable && <PageTitle>Submission Reports</PageTitle>}

      {renderFilters(
        <Restricted resource={'download_submission_report'} method="GET">
          <ActionRow style={{ justifyContent: 'flex-start' }}>
            <Button type="default" onClick={() => downloadSubmissionReport()}>
              <Icon type="download" /> Download report
            </Button>
          </ActionRow>
        </Restricted>
      )}

      <div style={{ overflowX: 'auto' }}>
        <AsyncTable
          resource={resource}
          pagination
          config={{
            ...config,
            table: [
              ...config.table,
              {
                title: 'Location',
                render: (_, { locationType }) => {
                  return locationType === 1 ? 'Yard' : 'Classroom';
                }
              },
              {
                title: 'Branch',
                render: (_, { branchId, locationType }) => {
                  const branchName = locations.find(loc => branchId === loc.branchId);
                  return (branchName?.label || '').split('Branch')[0];
                }
              }
            ]
          }}
          fileName={resource}
          params={filterParams}
          onChange={(pagination, filters, sorter) => {
            console.log(sorter.field);
            if (!!sorter.field) {
              setFilterParams(prev => ({
                ...prev,
                orderBy: camelToSnakeCase(
                  sorter.field === 'studentFirstName'
                    ? 'studentFullName'
                    : sorter.field === 'submittedByFirstName'
                      ? 'submittedByFullName'
                      : sorter.field
                ),
                order: sorter.order === 'ascend' ? 'asc' : 'desc'
              }));
            }
          }}
        />
      </div>
    </PageWrapper>
  );
};

export default props => {
  return <StandaloneSubmissionReport isReusable={props.isReusable} />;
};
